import { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { ClipboardList, MapPin, Star, CheckSquare, Activity, AlertTriangle, TrendingUp, Bell } from 'lucide-react';

function StatCard({ label, value, icon: Icon, gradient, sub }) {
  return (
    <div className={`stat-card ${gradient}`}>
      <div>
        <div className="stat-card-label">{label}</div>
        <div className="stat-card-value">{value ?? '—'}</div>
        {sub && <div style={{ fontSize: 11, opacity: 0.75, marginTop: 6 }}>{sub}</div>}
      </div>
      <Icon className="stat-card-icon" size={52} />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [ozet, setOzet] = useState(null);
  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bitis = new Date().toISOString().slice(0, 10);
    const baslangic = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    Promise.all([
      api.get('/kullanicilar/dashboard/ozet'),
      api.get('/raporlar/anket', { params: { baslangic } }).catch(() => null),
    ]).then(([o, r]) => {
      setOzet(o.data);
      if (r) setRapor(r.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <div className="spinner spinner-blue" style={{ width: 28, height: 28 }} />
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{user?.kurum_adi || 'Sistem'} - Genel Bakış</p>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16, marginBottom: 22 }}>
        <StatCard
          label="Toplam Anket"
          value={ozet?.toplam_anket?.toLocaleString('tr') ?? 0}
          sub="Tüm zamanlar"
          icon={Bell}
          gradient="gradient-blue"
        />
        <StatCard
          label="Aktif Lokasyon"
          value={ozet?.aktif_lokasyon ?? 0}
          sub="Şu an aktif"
          icon={MapPin}
          gradient="gradient-green"
        />
        <StatCard
          label="Ort. Puan"
          value={ozet?.ortalama_puan ? `${Number(ozet.ortalama_puan).toFixed(1)}/5` : '—'}
          sub="Genel memnuniyet"
          icon={Star}
          gradient="gradient-orange"
        />
        <StatCard
          label="Bugünkü Anket"
          value={ozet?.bugunki_anket ?? 0}
          sub="Son 24 saat"
          icon={Activity}
          gradient="gradient-purple"
        />
        <StatCard
          label="Bugünkü Checklist"
          value={ozet?.bugunki_checklist ?? 0}
          sub="Bugün yapılan"
          icon={CheckSquare}
          gradient="gradient-cyan"
        />
      </div>

      {/* Uyarı */}
      {rapor?.ozet?.dusuk_puan > 0 && (
        <div className="alert alert-error" style={{ marginBottom: 18 }}>
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          <span><strong>{rapor.ozet.dusuk_puan}</strong> anket 3 puanın altında değerlendirme aldı — dikkat gerektiriyor!</span>
        </div>
      )}

      {/* Grafikler */}
      {rapor && (
        <div className="chart-grid">
          <div className="card" style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
              <TrendingUp size={15} color="var(--blue)" />
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>Günlük Anket Trendi (30 gün)</span>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={rapor.gunluk || []}>
                <defs>
                  <linearGradient id="aG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4a6cf7" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#4a6cf7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="tarih" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} tickFormatter={d => d?.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <Tooltip
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  formatter={v => [v, 'Anket']}
                  labelFormatter={l => `Tarih: ${l}`}
                />
                <Area type="monotone" dataKey="sayi" stroke="#4a6cf7" strokeWidth={2} fill="url(#aG)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
              <MapPin size={15} color="var(--blue)" />
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>Lokasyon Bazlı Anket</span>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={(rapor.lokasyon_bazli || []).slice(0, 7)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis dataKey="lokasyon" type="category" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} width={80} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="anket_sayisi" fill="#4a6cf7" radius={[0,4,4,0]} name="Anket" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Son bildirimler tablosu */}
      {rapor?.lokasyon_bazli?.length > 0 && (
        <div className="card">
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <ClipboardList size={15} color="var(--blue)" />
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>Lokasyon Memnuniyet Özeti</span>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Lokasyon</th>
                  <th>Bina</th>
                  <th>Anket Sayısı</th>
                  <th>Ort. Puan</th>
                </tr>
              </thead>
              <tbody>
                {rapor.lokasyon_bazli.slice(0, 10).map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{r.lokasyon}</td>
                    <td>{r.bina || '—'}</td>
                    <td><span className="badge badge-blue">{r.anket_sayisi}</span></td>
                    <td>
                      <span className={`badge ${r.ort_puan >= 4 ? 'badge-green' : r.ort_puan >= 3 ? 'badge-yellow' : 'badge-red'}`}>
                        {r.ort_puan ? Number(r.ort_puan).toFixed(1) : '—'} / 5
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
